import { useGetAccountMembershipUsageByLicenseTypeQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';

/**
 * Determines whether an access request can be approved based on account user status and seat availability.
 *
 * This hook checks two conditions to determine if an access request can be approved:
 * 1. The requester already has a member license (no new seat needed)
 * 2. There are available seats in the account (either no hard limit is enforced or seats are available)
 *
 * @param {boolean | null | undefined} params.hasMemberLicense - Whether the requester already has a member license.
 * If true, approval doesn't require an available seat.
 *
 * @returns {boolean} Whether the access request can be approved. Returns true if the
 * requester has an account user OR if there are available seats in the account.
 *
 * @example
 * // User already has account access
 * const { canApprove } = useCanApproveAccessRequest({ hasAccountUser: true });
 * // canApprove will be true
 *
 */
export const useCanApproveAccessRequest = ({
  hasMemberLicense,
}: {
  hasMemberLicense: boolean | null | undefined;
}) => {
  const accountId = useActiveAccountId();
  const { data: accountUsage } = useGetAccountMembershipUsageByLicenseTypeQuery(
    {
      variables: { accountId },
      skip: !accountId,
    }
  );

  const availableSeats =
    accountUsage?.account?.usageByLicenseType?.member?.availableSeats ?? 0;

  const hasAvailableSeats =
    !accountUsage?.account?.usageByLicenseType?.member?.isHardLimit ||
    availableSeats > 0;

  const canApprove = hasMemberLicense || hasAvailableSeats;

  return {
    canApprove,
  };
};
